import { type ReactNode, type RefObject } from "react";
import { styled } from "@pigment-css/react";
import { NameText } from "./ui/NameText";
import { BatchCycler } from "./BatchCycler";

// TopNav center pill — the customer name in writing mode (also the morph target).
const NamePill = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  maxWidth: "100%", // never wider than the TopNav centre track → never covers X / skip
  minWidth: 0,
  height: theme.fieldSalesTwo.spacing[48],
  padding: `0 ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius.circular,
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  border: `1px solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  cursor: "pointer",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[900],
}));

// Center-slot stack: the cycler and the name pill share one grid cell so they
// overlap (cycler underneath, "N left" visible during the flight) while both stay
// capped to the centre track — so neither can overflow onto the X / skip buttons.
const TitleStack = styled("div")({
  display: "grid",
  width: "100%",
  minWidth: 0,
  justifyItems: "center",
  alignItems: "center",
});

const TitleLayer = styled("div")({
  gridArea: "1 / 1",
  minWidth: 0,
  maxWidth: "100%",
  display: "flex",
  justifyContent: "center",
});

interface TitleBarProps {
  isMulti: boolean;
  writing: boolean;
  name: string;
  itemCount: number;
  pillRef: RefObject<HTMLButtonElement | null>;
  flightHidden: "card" | "pill" | null;
  onPrev: () => void;
  onNext: () => void;
  onExitWriting: () => void;
}

export function TitleBar({
  isMulti,
  writing,
  name,
  itemCount,
  pillRef,
  flightHidden,
  onPrev,
  onNext,
  onExitWriting,
}: TitleBarProps): ReactNode {
  const pillVisibility = { visibility: flightHidden === "pill" ? ("hidden" as const) : ("visible" as const) };

  // Multi: the cycler stays mounted underneath (inert while writing) so "N left" is
  // visible during the flight; the name pill overlays it. Single: just the name pill.
  if (isMulti) {
    return (
      <TitleStack>
        {!writing && (
          <TitleLayer style={{ pointerEvents: writing ? "none" : "auto" }}>
            <BatchCycler count={itemCount} onPrev={onPrev} onNext={onNext} />
          </TitleLayer>
        )}
        {writing && (
          <TitleLayer style={{ zIndex: 1 }}>
            <NamePill ref={pillRef} type="button" onClick={onExitWriting} style={pillVisibility}>
              <NameText>{name}</NameText>
            </NamePill>
          </TitleLayer>
        )}
      </TitleStack>
    );
  }

  if (writing) {
    return (
      <NamePill ref={pillRef} type="button" onClick={onExitWriting} style={pillVisibility}>
        <NameText>{name}</NameText>
      </NamePill>
    );
  }

  return undefined;
}
